import { LocationSource } from '@/signalr';
import type { PortalCoordinateLocation } from '@/signalr';
import type { LatLng, MapPoint } from './mapModel';

export type MarkerKind =
  | 'aed'
  | 'amlPositionSource'
  | 'callerLocation'
  | 'caller'
  | 'appleEedPositionSource'
  | 'home'
  | 'incident'
  | 'location'
  | 'manualPositionSource'
  | 'mobilePositionSource'
  | 'webAppPositionSource';

export type MapMarker = {
  kind: MarkerKind;
  markerId: string;
  latLng: LatLng;
  content: string | null;
  markerIconUrl?: string;
  iconSize?: MapPoint;
  iconAnchor?: MapPoint;
  popupAnchor?: MapPoint;
  locationSource?: LocationSource;
};

const POSITION_SOURCE_ICON_ANCHOR: MapPoint = [15, 22];

export function aedMarker(markerId: string, latLng: LatLng, content: string): MapMarker {
  return { kind: 'aed', markerId, latLng, content, markerIconUrl: 'Images/Icons/map-aed.svg' };
}

export function amlPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'amlPositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-aml.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    locationSource: LocationSource.Aml,
  };
}

export function callerLocationMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'callerLocation',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-selected.svg',
    iconSize: [49, 70],
    iconAnchor: [24, 60],
    popupAnchor: [0, -62],
  };
}

export function callerMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'caller',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-caller.svg',
    iconSize: [45, 62],
    iconAnchor: [22, 62],
    popupAnchor: [0, -62],
  };
}

export function appleEedPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'appleEedPositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-apple-eed.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    locationSource: LocationSource.AppleEed,
  };
}

export function homeMarker(markerId: string, latLng: LatLng): MapMarker {
  return { kind: 'home', markerId, latLng, content: null, markerIconUrl: 'Images/Icons/map-home.svg' };
}

export function incidentMarker(markerId: string, latLng: LatLng): MapMarker {
  return { kind: 'incident', markerId, latLng, content: null, markerIconUrl: 'Images/Icons/map-incident.svg' };
}

export function locationMarker(markerId: string, latLng: LatLng, content: string): MapMarker {
  return { kind: 'location', markerId, latLng, content, markerIconUrl: 'Images/Icons/map-icon.svg' };
}

export function manualPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'manualPositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-manual.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    locationSource: LocationSource.Entered,
  };
}

export function mobilePositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'mobilePositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-device.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    locationSource: LocationSource.MobileApp,
  };
}

export function mobileSmsPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'mobilePositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-device.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    locationSource: LocationSource.MobileAppSms,
  };
}

export function googleElsPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'mobilePositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-device.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    locationSource: LocationSource.GoogleEls,
  };
}

export function webAppPositionSourceMarker(markerId: string, latLng: LatLng): MapMarker {
  return {
    kind: 'webAppPositionSource',
    markerId,
    latLng,
    content: null,
    markerIconUrl: 'Images/Icons/map-device.svg',
    iconAnchor: POSITION_SOURCE_ICON_ANCHOR,
    locationSource: LocationSource.WebApp,
  };
}

export function isPositionSourceMarker(marker: MapMarker): boolean {
  return marker.locationSource !== undefined;
}

export function createMarkerBySource(location: PortalCoordinateLocation): MapMarker {
  const position: LatLng = { lat: location.latitude, lng: location.longitude };
  switch (location.source) {
    case LocationSource.Entered:
      return manualPositionSourceMarker('ManualPositionSourceMarker', position);
    case LocationSource.Aml:
      return amlPositionSourceMarker('AmlPositionSourceMarker', position);
    case LocationSource.AppleEed:
      return appleEedPositionSourceMarker('AppleEedPositionSourceMarker', position);
    case LocationSource.WebApp:
      return webAppPositionSourceMarker('WebAppPositionSourceMarker', position);
    case LocationSource.MobileApp:
      return mobilePositionSourceMarker('MobilePositionSourceMarker', position);
    case LocationSource.MobileAppSms:
      return mobileSmsPositionSourceMarker('MobileSmsPositionSourceMarker', position);
    case LocationSource.GoogleEls:
      return googleElsPositionSourceMarker('GoogleElsPositionSourceMarker', position);
  }

  const unknownSource: never = location.source;
  throw new Error(`${String(unknownSource)} is unknown LocationSource`);
}
